import { client } from "../../hooks/Client"
import { useHistory } from "react-router-dom";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import useSettings from "../../hooks/Settings";
import usePersonaTerms from "../../hooks/usePersonaTerms";
import Tooltip from "../../components/common/Tooltip";
import { useFirestoreOrderBy } from "../../firebase/useFirestore";
import { useState, useContext, useEffect } from "react";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import { db, timestamp } from "../../firebase/config.js"
import { v4 as uuid } from 'uuid';
import DeleteModal from "../../components/common/DeleteModal";
import { Auth } from "../../StateManagment/Auth";
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import PageHeader from "../../components/layout/PageHeader.jsx";
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import TableDragHandleCell from "../../components/common/table/TableDragHandleCell.jsx";
import TargetgroupMeta from "../../components/stakeholders/TargetgroupMeta";
import TableTitleCell from "../../components/common/table/TableTitleCell.jsx";
import { isResearchForAll } from "../../helpers/measurementSignals/effectiveResearchIds";
import isFlagOn from "../../helpers/isFlagOn";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import useDataQualityScore from "../../hooks/useDataQualityScore";
import { dataQualityBand } from "../../helpers/dashboard/dataQualityDisplay";
import Modal from "../../components/common/Modal";
import buildNewResearchDocs from "../../helpers/research/newResearchDocs";
import { researchDeleteOptions, researchDeleteTargets } from "../../helpers/research/planResearchDelete";
import softDeleteDoc from "../../helpers/softDeleteDoc";
import { buildShareQuestionnairePath } from "../../helpers/questionnaires/shareQuestionnairePath";

// Datakwaliteit per onderzoek — de bevroren nachtelijke subscore uit
// DataQualityScores.PerResearch. Onderzoeken zonder metingen tonen '—'.
const renderResearchQuality = (entry, loading) => {
    if (!entry) return loading ? '…' : '—'
    return (
        <Tooltip content={`Datakwaliteit van dit onderzoek (${entry.Records} antwoorden)`} width='120%' top='-60px'>
            <span className={`data-quality-score--${dataQualityBand(entry.Score)}`}>
                {entry.Score}
            </span>
        </Tooltip>
    )
}

/**
 * De labels in de kolom 'INSTELLINGEN' (tot 2026-10-28 'GELDT VOOR'): voor wie het onderzoek geldt, en hoe er
 * ingevuld wordt (eerst naam of adres, of een gesprek met de AI-interviewer).
 * @param {object} research - Research-document
 * @param {import('../../helpers/personaTerms').PersonaTerms} terms
 * @returns {string[]}
 */
const researchPills = (research, terms) => [
    isResearchForAll(research, 'adress') && 'alle huishoudens',
    isResearchForAll(research, 'persona') && `alle ${terms.pluralLower}`,
    isFlagOn(research?.AskAddressFirst) && 'adres gevraagd',
    isFlagOn(research?.AskNameFirst) && 'naam gevraagd',
    isFlagOn(research?.AIInterviewer) && 'AI-interviewer',
].filter(Boolean)

const Research = () => {
    // Context
    const [saved, setSaved] = useContext(SavedIcon)
    // FIXtool zegt 'medewerkers', de rest 'deelnemers'.
    const personaTerms = usePersonaTerms()
    const [auth] = useContext(Auth)

    // State
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteDocid, setDeleteDocid] = useState('123')
    const [deleteName, setDeleteName] = useState('')
    const [researchID, setResearchID] = useState('')
    const [dragArray, setDragArray] = useState([]);
    const [newModalOpen, setNewModalOpen] = useState(false)
    const [newTitle, setNewTitle] = useState('')
    // Wat er aan het te verwijderen onderzoek hangt, opgehaald bij het openen
    // van het venster zodat de vinkjes een echt aantal kunnen noemen.
    const [pendingDelete, setPendingDelete] = useState(
        /** @type {{research: object, moments: object[], tasks: object[]} | null} */ (null)
    )

    // Hooks
    const history = useHistory()
    const subscription = useSettings().subscription

    // Firestore
    const researches = useFirestoreOrderBy('Research', 'Position', 'asc')
    const questionnaires = useFirestoreOrderBy('Questionnaires', 'Position', 'asc')
    const { stored: qualityDoc, loading: qualityLoading } = useDataQualityScore()

    // Whenever Firestore data changes, update dragArray state.
    // Soft-deleted researches (ADR-008) stay in Firestore but are hidden here.
    useEffect(() => {
        setDragArray((researches || []).filter((research) => !research.Deleted));
    }, [researches]);

    // DRAG & DROP: reorder items and update Firestore
    const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    // Update local state to reflect the reorder immediately
    setDragArray(items);

    // Now update Firestore with new positions
    items.forEach((dragArray, idx) => {
        db.collection('Research')
        .doc(dragArray.docid) // assuming .docid is stored for each doc
        .update({ Position: idx + 1 });
    });

    // Show "saved" icon
        setSaved('flex');
    };

    // Levende documenten van één collectie die aan dit onderzoek hangen.
    // Met CompagnyID: een verse fetch valt buiten de hooks en hun tenantfilter.
    // AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
    const fetchLinked = async (collection, id) => {
        const snap = await db.collection(collection)
            .where('CompagnyID', '==', client)
            .where('ResearchID', '==', id)
            .get()
        return snap.docs
            .map((docSnap) => ({ ...docSnap.data(), docid: docSnap.id }))
            .filter((doc) => !doc.Deleted)
    }

    // Delete modal
    const deleteModalHandler = async (e) => {
        // currentTarget, not target: clicks land on the <path> inside the MUI
        // icon, which carries none of the data-attributes.
        const docid = e.currentTarget.dataset.docid
        const deleteName = e.currentTarget.dataset.deletename
        const id = e.currentTarget.dataset.id

        setDeleteDocid(docid)
        setDeleteName(deleteName)
        setResearchID(id)

        const research = dragArray.find((r) => r.ID === id) || { ID: id, docid, Title: deleteName }
        const [moments, tasks] = await Promise.all([fetchLinked('MeasureMoments', id), fetchLinked('Tasks', id)])
        setPendingDelete({ research, moments, tasks })
        setDeleteModal(true)
    }

    // Wat het verwijdervenster aanbiedt: geplande taken, en de vragenlijst als
    // geen ander onderzoek hem gebruikt. Tot 2026-09-15 bleven die stil staan.
    const linkedOptions = pendingDelete
        ? researchDeleteOptions({
            research: pendingDelete.research,
            researches: dragArray,
            questionnaires: questionnaires || [],
            taskCount: pendingDelete.tasks.length,
        })
        : []

    // Delete research (soft delete, ADR-008). Alles wat meegaat krijgt het
    // onderzoek als ouder, zodat de prullenbak het als één regel terugzet.
    const deleteResearch = async (selectedKeys = []) => {
        const research = pendingDelete?.research || { ID: researchID, docid: deleteDocid, Title: deleteName }
        await softDeleteDoc({ collection: 'Research', docid: research.docid, authId: auth?.ID, title: research.Title })

        const exclusief = linkedOptions.find((o) => o.key === 'questionnaire')?.count > 0
        const questionnaire = exclusief ? (questionnaires || []).find((q) => q.ID === research.QuestionnaireID) : null
        const targets = researchDeleteTargets({
            moments: pendingDelete?.moments || [],
            tasks: pendingDelete?.tasks || [],
            questionnaire,
            selectedKeys,
        })
        for (const target of targets) {
            await softDeleteDoc({ collection: target.collection, docid: target.docid, authId: auth?.ID, title: target.title, parentId: research.docid })
        }
        setPendingDelete(null)
        setSaved('flex')
    }

    // Add a research and auto-create a questionnaire that is linked to it
    // (Research.QuestionnaireID — the same field selectQuestionnaire writes).
    // Pas na het venster 'Nieuw onderzoek': één klik op de plusknop maakte
    // anders twee documenten aan zonder naam (testronde 2026-09-15, B1).
    const addResearch = async () => {
        const { research, questionnaire } = buildNewResearchDocs({
            title: newTitle,
            // Count only visible (non-deleted) researches for position + numbering
            researchCount: dragArray.length,
            questionnaireCount: questionnaires?.length || 0,
            client,
            timestamp,
            researchID: uuid(),
            questionnaireID: uuid(),
        })

        const batch = db.batch()
        batch.set(db.collection('Questionnaires').doc(), questionnaire)
        batch.set(db.collection('Research').doc(), research)
        await batch.commit()

        setNewModalOpen(false)
        setNewTitle('')
        setSaved('flex')
    }

    // Copy a research — clones the Research doc and its MeasureMoments,
    // keeping QuestionnaireID and TargetgroupID so the copy is ready to use.
    // Tasks (separate collection keyed by MeasureMomentID) are intentionally not copied.
    const copyResearch = async (e) => {
        const sourceID = e.currentTarget.dataset.id

        const source = dragArray.find((r) => r.ID === sourceID)
        if (!source) return

        const newResearchID = uuid()
        // eslint-disable-next-line no-unused-vars
        const { docid: _docid, ID: _ID, Position: _Position, Title: sourceTitle, Timestamp: _Timestamp, ...rest } = source

        await db.collection('Research').doc().set({
            ...rest,
            ID: newResearchID,
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
            Title: `Kopie van ${sourceTitle || 'onderzoek'}`,
            Position: dragArray.length + 1,
        })

        const momentsSnap = await db.collection('MeasureMoments')
            .where('ResearchID', '==', sourceID)
            .get()

        // Chunk at 400 to stay under Firestore's 500-op batch limit.
        // Soft-deleted moments (ADR-008) stay behind — the copy only gets live ones.
        const docs = momentsSnap.docs.filter((docSnap) => !docSnap.data().Deleted)
        for (let i = 0; i < docs.length; i += 400) {
            const batch = db.batch()
            docs.slice(i, i + 400).forEach((docSnap) => {
                const moment = docSnap.data()
                const ref = db.collection('MeasureMoments').doc()
                batch.set(ref, {
                    ...moment,
                    ID: uuid(),
                    ResearchID: newResearchID,
                    Timestamp: timestamp,
                    Responses: 0,
                })
            })
            await batch.commit()
        }

        setSaved('flex')
    }

    const changeResearchTitle = (e) => {
        const docid = e.target.dataset.docid
        const title = e.target.value

        db.collection('Research')
        .doc(docid)
        .update({
            Title: title
        })
        .then(() => {
            setSaved('flex')
         })
    }

  return (
     <div className="main">
    <div className="main-container">
        <PageHeader 
            heading={'Onderzoeken'}
            description={`Hier kun je onderzoeken aanmaken die je kunt gebruiken om de impact van je interventies te meten. Klik op de plusknop onder de tabel om een onderzoek toe te voegen.`}
            AI={true}
        />
        {/* <PageNotice 
            text={<span dangerouslySetInnerHTML={{ __html: 'Onderzoeken moeten worden ingesteld voordat je verder kunt. Dat doe je door op aanpassen te klikken in de tabel.' }} />}
        /> */}
        <ReadOnlyGuard edit="measurement.edit">
            {(canEdit) => (
                <Table
                    items={dragArray || []}
                    columns={[
                        { key: 'drag', label: '', searchable: false },
                        { key: 'title', label: 'ONDERZOEK', searchAccessor: (research) => research.Title || '' },
                        { key: 'targetgroup', label: 'DOELGROEP', searchable: false },
                        { key: 'appliesTo', label: 'INSTELLINGEN', searchable: false },
                        { key: 'quality', label: 'DATAKWALITEIT', searchable: false },
                        { key: 'start', label: 'ONDERZOEK STARTEN', searchable: false },
                        { key: 'results', label: 'RESULTATEN', searchable: false },
                        { key: 'copy', label: 'KOPIEREN', searchable: false },
                        { key: 'delete', label: 'VERWIJDER', searchable: false },
                    ]}
                    draggable
                    droppableId="research"
                    onDragEnd={handleDragEnd}
                    emptyMessage="Geen onderzoeken gevonden."
                    getItemKey={(research) => research.ID}
                    searchPlaceholder="Zoeken op onderzoek"
                    addRow={canEdit ? {
                        toolTipContent: subscription === 'free' ? 'Extra onderzoeken toevoegen is een premium functie.' : 'Onderzoek toevoegen',
                        onClick: subscription === 'free' ? null : () => setNewModalOpen(true),
                    } : undefined}
                    renderRow={(research) => (
                        <>
                            <TableDragHandleCell>
                                <DragIndicatorOutlinedIcon />
                            </TableDragHandleCell>
                            <TableTitleCell
                                value={research.Title}
                                to={`/${client}/researchbuilder/${research.ID}`}
                                onBlur={changeResearchTitle}
                                docid={research.docid}
                                placeholder='Titel van onderzoek'
                            />
                            <TableCell>
                                {research.TargetgroupID
                                    ? <TargetgroupMeta targetgroupID={research.TargetgroupID} />
                                    : <p>-</p>}
                            </TableCell>
                            <TableCell>
                                {/* Dezelfde labels als op de Onderzoeken-tabs van huishouden en
                                    medewerker; de schakelaars zelf staan in de onderzoeksbouwer. */}
                                {/* Ook hoe er ingevuld wordt: Rob opende vijf onderzoeken om er
                                    één zonder AI-interviewer te vinden (testronde 2026-10-13, B4). */}
                                {researchPills(research, personaTerms).map((label) => (
                                    <span key={label} className="measurement-signal-pill">{label}</span>
                                ))}
                                {researchPills(research, personaTerms).length === 0 && <p>-</p>}
                            </TableCell>
                            <TableCell>
                                {renderResearchQuality(qualityDoc?.PerResearch?.[research.ID], qualityLoading)}
                            </TableCell>
                            <TableCell>
                                <SendOutlinedIcon className="table-delete-icon" onClick={() => history.push(buildShareQuestionnairePath({ client, questionnaireID: research.QuestionnaireID, researchID: research.ID }))}/>
                            </TableCell>
                            <TableCell>
                                <BarChartOutlinedIcon className="table-delete-icon" onClick={() => history.push(`/${client}/researchresults/${research.ID}`)}/>
                            </TableCell>
                            <TableCell>
                                <Tooltip content={'Onderzoek kopiëren'} width='80%' top='-60px'>
                                    <ContentCopyOutlinedIcon className='table-delete-icon' data-id={research.ID} onClick={copyResearch} />
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                {canEdit && (
                                    <Tooltip content={'Verwijder onderzoek'} width='80%' top='-60px'>
                                        <DeleteOutlinedIcon className='table-delete-icon' data-testid='delete-research' data-id={research.ID} data-docid={research.docid} data-deletename={research.Title} onClick={deleteModalHandler} />
                                    </Tooltip>
                                )}
                            </TableCell>
                        </>
                    )}
                />
            )}
        </ReadOnlyGuard>
        <DeleteModal deleteModal={deleteModal} setDeleteModal={setDeleteModal} deleteItem={deleteResearch} deleteName={deleteName}
            linkedOptions={linkedOptions}
            softDelete
        />
        <Modal
            modalOpen={newModalOpen}
            setModalOpen={setNewModalOpen}
            pageHeader='Nieuw onderzoek'
            confirm={addResearch}
            confirmDisabled={!newTitle.trim()}
            confirmLabel='Toevoegen'
            content={
                <div className='research-new-modal'>
                    <label htmlFor='research-new-title'>Titel van het onderzoek</label>
                    <input
                        id='research-new-title'
                        type='text'
                        value={newTitle}
                        placeholder='Bijvoorbeeld: Kwartaalmeting deelnemers'
                        onChange={(e) => setNewTitle(e.target.value)}
                    />
                </div>
            }
        />
      </div>
    </div>
  )
}

export default Research
